<template>
<div>
  <div>
    <div class="h-panel">
      <b-button
        v-if="mayManage"
        :to="{name: 'regatta_add'}"
        variant="primary"
      >
        Новая регата
      </b-button>

      <b-button
        :disabled="isDeleteRegattasButtonDisabled"
        :variant="deleteRegattasButtonVariant"
        @click="deleteRegattasWithConfirmation"
      >
        Удалить
      </b-button>
    </div>

    <h2>Регаты</h2>

    <table class="mb-2">
      <tr>
        <th class="pr-1">
          <label for="status-selector" class="mb-0">Статус</label>
        </th>
        <td class="pr-4">
          <b-form-select
              id="status-selector"
              v-model="status"
              :options="statusOptions"
          />
        </td>

        <th class="pr-1">
          <label for="window-past-duration-selector" class="mb-0">Прошлое</label>
        </th>
        <td class="pr-4">
          <b-form-select
              id="window-past-duration-selector"
              v-model="windowPastDuration"
              :options="windowDurationOptions"
          />
        </td>

        <th class="pr-1">
          <label for="window-future-duration-selector" class="mb-0">Будущее</label>
        </th>
        <td>
          <b-form-select
              id="window-future-duration-selector"
              v-model="windowFutureDuration"
              :options="windowDurationOptions"
          />
        </td>
      </tr>
    </table>

    <p v-if="message !== null">
      {{message}}
    </p>

    <table v-if="hasRegattas" class="table table-bordered">
      <thead class="thead-dark">
        <tr>
          <th rowspan="2" class="text-nowrap">
            <input type="checkbox" v-model="allSelected" class="mr-2">Ид
          </th>
          <th rowspan="2">Аудитория</th>
          <th rowspan="2">Статус</th>
          <th rowspan="2">Начало<br><small>UTC+3</small></th>
          <th rowspan="2">Реальное<br>начало<br><small>UTC+3</small></th>
    <!--  <th>Join available until<br><small>UTC+3</small></th>  -->
          <th rowspan="2">Конец<br><small>UTC+3</small></th>
          <th rowspan="2">Реальный<br>конец<br><small>UTC+3</small></th>
          <th rowspan="2">Миним.<br>версия<br>клиента</th>
<!--          <th>Leagues</th>-->
          <th rowspan="2">Создание</th>
          <th colspan="2" class="text-center">АБ-тест</th> 
          <!-- <th>Rounds</th> -->
          <th rowspan="2">Действия</th>
        </tr>
        <tr>
          <th>прототип</th> <!-- AB test proto id -->
          <th>группа</th>   <!-- AB test group -->
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="container in regattaContainers"
          :key="container.regatta.id"
          :class="getRegattaClass(container.regatta.status)"
        >
          <td class="text-nowrap">
            <input type="checkbox" v-model="container.selected" class="mr-2">
            <regatta-link :regattaId="container.regatta.id" label="" />
          </td>
          <td>{{ container.regatta.audience | getRegattaAudienceNameVerbose }}</td>
          <td :class="getRegattaStatusClass(container.regatta.status)">
            {{ container.regatta.status | statusStr }}
          </td>
          <td>{{ container.regatta.start_at | getMomentMinStr }}</td>
          <td>{{ container.regatta.actual_start_at | getMomentMinStr }}</td>
<!--          <td>{{ container.regatta.finish_at - container.regatta.prohibition_interval | getMomentMinStr }}</td>-->
          <td>{{ container.regatta.finish_at | getMomentMinStr }}</td>
          <td>{{ container.regatta.actual_finish_at | getMomentMinStr }}</td>
          <td>{{ container.regatta.min_version | convertVersionCodeToStr }}</td>
          <td>{{ container.regatta.created_at | getMomentMinStr }}</td>
          <td>{{ container.regatta.ab_test_id | getHumanProtoAlias }}</td>
          <td>{{ container.regatta.ab_test_group }}</td>
          <!--          <td>-->
<!--            <b-link :to="{name: 'regatta_leagues', params: { regattaId: container.regatta.id }}">view</b-link>-->
<!--          </td>-->

          <!--
          <td>
            <b-link :to="{name: 'regatta_rounds', params: { regattaId: container.regatta.id }}">view</b-link>
          </td>
          -->

          <td class="action-box">
            <b-button size="sm" variant="danger"
              v-if="mayDelete(container.regatta)"
              @click="deleteRegattaWithConfirmation(container.regatta)">
              Удалить
            </b-button>
            <b-button size="sm" variant="warning"
              v-if="mayFinish(container.regatta)"
              @click="finishRegattaWithConfirmation(container.regatta)">
              Завершить
            </b-button>
          </td>
        </tr>
      </tbody>
    </table>

    <loading-indicator v-if="isLoading"/>
  </div>
</div>
</template>

<script>
import LoadingIndicator from '../LoadingIndicator.vue';
import RegattaLink from './RegattaLink.vue';

import auth from '../../auth.js';
import { HTTP } from '../../http-common.js';
import notifier from '../../notifier.js';

import {getBegin, getEnd, TIME_UNIT_MONTH} from '../../model/event_time_filter.js';
import { getWindowDurationOptions } from '../../model/event_time_filter.js';

/**
 * @typedef { import('../../service/confirm.js').ConfirmHandle } ConfirmHandle
 */
import { confirmAction } from '../../service/confirm.js';

import {convertMomentToStamp, getHumanProtoAlias, isRoleAdmin} from '../../util.js';
import {getRegattaAudienceNameVerbose, getRegattaStatusOptionsForFilter, Regatta} from "@/model/regatta";
import { getRegattaClass       } from '../../model/regatta.js';
import { getRegattaStatusClass } from '../../model/regatta.js';

const LOADING_STATUS__INIT    = 1;
const LOADING_STATUS__LOADING = 2;
const LOADING_STATUS__NORMAL  = 3;
const LOADING_STATUS__ERROR   = 4;

export default {
  name: 'RegattaList',

  components: {
    LoadingIndicator,
    RegattaLink,
  },

  data() {
    const DURATION_1_MONTH = {timeUnit: TIME_UNIT_MONTH, amount: 1};

    return {
      actor: auth.getViewer(),

      loadingStatus: LOADING_STATUS__INIT,

      regattaContainers: null,

      statusOptions: getRegattaStatusOptionsForFilter(),
      status: 3, // hardcode for any type regattas

      windowDurationOptions: getWindowDurationOptions(),
      windowPastDuration:   DURATION_1_MONTH,
      windowFutureDuration: DURATION_1_MONTH,
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadingStatus === LOADING_STATUS__LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadingStatus === LOADING_STATUS__NORMAL;
    },

    /** @returns {?string} */
    message() {
      if(this.isLoadedNormal && !this.hasRegattas) {
        return 'No regattas.';
      }

      return null;
    },

    hasRegattas() {
      const containers = this.regattaContainers;
      if(containers === null) {
        return false;
      }
      return containers.length > 0;
    },

    /** @returns {boolean} */
    mayManage() {
      return isRoleAdmin(this.actor.role);
    },

    //-------------------------------------------------------------------------
    // Selection
    //-------------------------------------------------------------------------

    /** @returns {boolean} */
    hasSelected() {
      const containers = this.regattaContainers;
      if(containers === null) {
        return false;
      }

      for(const container of containers) {
        if(container.selected) {
          return true;
        }
      }
      return false;
    },

    allSelected: {
      get() {
        for(const container of this.regattaContainers) {
          if(!container.selected) {
            return false;
          }
        }
        return true;
      },

      set(newValue) {
        if(newValue) {
          this.selectAll();
        } else {
          this.deselectAll();
        }
      },
    },

    /** 
     * Идентификаторы выбранных скачек.
     * @returns {number[] | null} 
     */
    selectedRegattaIds() {
      const containers = this.regattaContainers;
      if(containers === null) {
        return null;
      }

      const ids = [];
      for(const container of containers) {
        if(container.selected) {
          ids.push(container.regatta.id);
        }
      }

      return ids;
    },

    //-------------------------------------------------------------------------
    // Deletion
    //-------------------------------------------------------------------------

    /** @returns {boolean} */
    isDeleteRegattasButtonDisabled() {
      return !this.hasSelected;
    },

    /** @returns {string} */
    deleteRegattasButtonVariant() {
        return this.hasSelected ? 'danger' : 'light';
    }

    //-------------------------------------------------------------------------
  },

  created() {
    this.loadRegattas();
  },

  watch: {
    status() {
      this.loadRegattas();
    },

    windowPastDuration() {
      this.loadRegattas();
    },

    windowFutureDuration() {
      this.loadRegattas();
    },
  },

  methods: {
    getHumanProtoAlias,
    loadRegattas() {
      notifier.deleteSenderNotifications(this);

      this.loadingStatus = LOADING_STATUS__LOADING;

      const status = this.status;

      const requestConf = {
        params: {}
      };

      if(status !== null) {
        requestConf.params.status = status;
      }

      const now = convertMomentToStamp(new Date());

      const windowPastDuration = this.windowPastDuration;
      if(windowPastDuration !== null) {
        requestConf.params.begin = getBegin(now, windowPastDuration);
      }

      const windowFutureDuration = this.windowFutureDuration;
      if(windowFutureDuration !== null) {
        requestConf.params.end = getEnd(now, windowFutureDuration);
      }

      HTTP.get('/regattas', requestConf)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        const regattas = data.regattas || [];
        this.regattaContainers = convertRegattasToRegattaContainers(regattas);
        this.loadingStatus = LOADING_STATUS__NORMAL;
      })
      .catch(err => {
        this.loadingStatus = LOADING_STATUS__ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    findRegattaById(regattaId) {
      const containers = this.regattaContainers;
      if(containers === null) {
        return null;
      }

      for(const container of containers) {
        const regatta = container.regatta;
        if(regatta.id === regattaId) {
          return regatta;
        }
      }

      return null;
    },

    getRegattaClass,
    getRegattaStatusClass,

    selectAll() {
      for(const container of this.regattaContainers) {
        if(!container.selected) {
          container.selected = true;
        }
      }
    },

    deselectAll() {
      for(const container of this.regattaContainers) {
        if(container.selected) {
          container.selected = false;
        }
      }
    },

    //-------------------------------------------------------------------------
    // Finish
    //-------------------------------------------------------------------------

    mayFinish(regatta) {
      return isRoleAdmin(this.actor.role) && Regatta.isActive(regatta.status);
    },

    finishRegattaWithConfirmation(regatta) {
      notifier.deleteSenderNotifications(this);

      confirmAction(
        this,
        'Are you sure you want to finish this regatta {env}?',
        'Finish',
        () => {
          this.finishRegatta(regatta);
        },
      );
    },

    finishRegatta(regatta) {
      notifier.deleteSenderNotifications(this);
      
      const url = `/regattas/${regatta.id}/finish`;
      HTTP.post(url)
      .then(response => {
        const result = response.data.result;
        if (!result) {
          notifier.showError("Finish the regatta failed!", {sender: this});
          return;
        }

        regatta.status = result.status;
        notifier.showSuccess('Regatta finished.');
      })
      .catch(e => {
        notifier.showError(e, {sender: this});
      });
    },

    //-------------------------------------------------------------------------
    // Deletion
    //-------------------------------------------------------------------------

    mayDelete(regatta) {
      return isRoleAdmin(this.actor.role) && ((Regatta.isNew(regatta.status) || Regatta.isFinished(regatta.status)));
    },

    findNotDeletableRegattaIdsFromList(regattaIds) {
      const notDeletableRegattaIds = [];

      for(const regattaId of regattaIds) {
        const regatta = this.findRegattaById(regattaId);
        if(!isRegattaDeletable(regatta)) {
          notDeletableRegattaIds.push(regatta.id);
        }
      }

      return notDeletableRegattaIds;
    },

    deleteRegattaWithConfirmation(regatta) {
      notifier.deleteSenderNotifications(this);

      confirmAction(
        this,
        getRegattaDeletionConfirmationMessage(regatta.id),
        'Delete',
        () => {
          this.deleteRegatta(regatta);
        },
      );
    },

    deleteRegatta(regatta) {
      const url = `/regatta/${regatta.id}/delete`;
      HTTP.post(url)
      .then(response => {
        const result = response.data.result;
        if (result.code) {
          notifier.showError(result.message, {sender: this});
          return;
        }

        deleteRegattaContainerFromArray(this.regattaContainers, regatta.id);
        notifier.showSuccess('Regatta deleted.');
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    //-------------------------------------------------------------------------

    deleteRegattasWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      const regattaIds = this.selectedRegattaIds;

      const notDeletableDerbyIds = this.findNotDeletableRegattaIdsFromList(regattaIds);
      if(notDeletableDerbyIds.length > 0) {
        notifier.showError('Можно удалить только запланированные и закончившиеся регаты. Если регаты активны, то их удалить нельзя.', {sender: this});
        return;
      }

      this.confirmRegattasDeletion(
        regattaIds,
        () => {
          this.deleteRegattas(regattaIds);
        }
      );
    },

    /**
     * @param {number[]} regattaIds
     * @param {ConfirmHandle} onConfirmed
     */
    confirmRegattasDeletion(regattaIds, onConfirmed) {
      const params = {};
      if(regattaIds.length > 1) {
        params.description = getRegattaIdsInfo(regattaIds);
      }

      confirmAction(
        this,
        getRegattasDeletionConfirmationMessage(regattaIds),
        'Delete',
        onConfirmed,
        params,
      );
    },

    /** @param {number[]} regattaIds */
    deleteRegattas(regattaIds) {
      const idsStr = regattaIds.join(',');

      const data = new FormData();
      data.set('ids', idsStr);

      HTTP.post('/regattas/delete', data)
      .then(() => {
        deleteRegattaContainersFromArray(this.regattaContainers, regattaIds);

        const message = getDeletionCompleteMessage(regattaIds.length);
        notifier.showSuccess(message);
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    //-------------------------------------------------------------------------
  },

  filters: {
    statusStr: status => Regatta.getStatusName(status),

    getRegattaAudienceNameVerbose,
  },
}

function convertRegattasToRegattaContainers(regattas) {
  const containers = [];

  for(const regatta of regattas) {
    const container = {
      regatta: regatta,
      selected: false,
    };
    containers.push(container);
  }

  return containers;
}

function isRegattaDeletable(regatta) {
  return Regatta.isNew(regatta.status) || Regatta.isFinished(regatta.status);
}

function getRegattaDeletionConfirmationMessage(id) {
  return `Are you sure you want to delete regatta ${id} {env}?`
}

function getRegattasDeletionConfirmationMessage(ids) {
  if(ids.length === 1) {
    const id = ids[0];
    return getRegattaDeletionConfirmationMessage(id);
  }

  const amount = ids.length;
  return `Are you sure you want to delete ${amount} regattas {env}?`;
}

function getRegattaIdsInfo(ids) {
  return 'Regatta ids: ' + ids.join(', ') + '.';
}

/**
 * @param {Object[]} containers 
 * @param {number[]} ids
 */
function deleteRegattaContainersFromArray(containers, ids) {
  for(const id of ids) {
    deleteRegattaContainerFromArray(containers, id);
  }
}

/**
 * @param {Object[]} regattaContainers
 * @param {number} regattaId
 */
 function deleteRegattaContainerFromArray(regattaContainers, regattaId) {
  const index = getRegattaContainerIndex(regattaContainers, regattaId);
  if(index !== null) {
    regattaContainers.splice(index, 1);
  }
}

/**
 * @param {Object[]} regattaContainers
 * @param {number} regattaId
 * @returns {number | null}
 */
 function getRegattaContainerIndex(regattaContainers, regattaId) {
  for(let i = 0; i < regattaContainers.length; i++) {
    const container = regattaContainers[i];
    if(container.regatta.id == regattaId) {
      return i;
    }
  }

  return null;
}

function getDeletionCompleteMessage(amount) {
  if(amount === 1) {
    return 'Regatta deleted.'
  } else {
    return 'Regattas deleted.'
  }
}
</script>

<style scoped>
.action-box {
  padding: 7px;
}

.action-box > .btn:last-child {
  margin-right: 0 !important;
}
</style>
